/**
 * Shared primitives for the live time-clock UI. Recreated from the visual reference
 * (claude_designs/worktime/reference/worktime-lib.jsx) with MUI. Live timers are computed
 * from the server's started_at on every render (via useNow) so the display always reconciles
 * to server truth — never a free-running client counter that can drift.
 */
import { plateFont } from '../../../utils/fonts';
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

// Visual language tokens (navy day clock, green WO clock, amber pause, red end).
export const TOKENS = {
  navy: '#1a365d',
  accent: '#3182ce',
  accentL: '#4299e1',
  tint: 'rgba(49,130,206,0.10)',
  green: '#16a34a',
  greenDark: '#15803d',
  greenSoft: '#dcfce7',
  amber: '#d97706',
  amberSoft: '#fef3c7',
  amberText: '#92400e',
  red: '#dc2626',
  redSoft: '#fee2e2',
  redBorder: '#fecaca',
  border: '#e2e8f0',
  bg: '#f8fafc',
  surface: '#ffffff',
  fg: '#1a202c',
  muted: '#64748b',
  subtle: '#94a3b8',
};

// HH:MM:SS for the large timers.
export function hms(totalSec) {
  const s = Math.max(0, Math.floor(totalSec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(ss)}`;
}

// Human-readable short form for accrued/contributor time. Pass the i18next `t` for localized units
// (Finnish "4 t 1 min"); without it falls back to "4h 1min" (e.g. non-React contexts).
export function hmShort(totalSec, t) {
  const s = Math.max(0, Math.floor(totalSec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (t) {
    return h > 0 ? `${h} ${t('h')} ${m} ${t('min')}` : `${m} ${t('min')}`;
  }
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

// 24-hour HH:MM from an ISO instant (Finnish workshops — never AM/PM). Locale-independent.
export function hhmm(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Re-renders every `intervalMs` so live elapsed (computed from started_at) updates smoothly
// between server polls. Display-only — the authoritative session list comes from polling.
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// Seconds elapsed since a server ISO timestamp, measured against `nowMs` (from useNow).
export function elapsedSeconds(startedAt, nowMs) {
  if (!startedAt) return 0;
  const start = new Date(startedAt).getTime();
  const ref = typeof nowMs === 'number' ? nowMs : Date.now();
  return Math.max(0, Math.floor((ref - start) / 1000));
}

// THE one formula for a live, cumulative day-presence clock: a non-ticking base (sum of today's
// already-closed day spans, computed server-side) + the running day session's live elapsed. Used
// identically by the clock page, the top-bar pill, and the manager board so they never disagree.
export function livePresenceSeconds(baseSeconds, runningStartedAt, nowMs) {
  return Math.max(0, baseSeconds || 0) + (runningStartedAt ? elapsedSeconds(runningStartedAt, nowMs) : 0);
}

// Local YYYY-MM-DD (org-day boundary is handled server-side; this is for the /day/<date> fetch).
export function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Finnish display date: 'YYYY-MM-DD' → 'D.M.YYYY' (no leading zeros). Falls back to input as-is.
export function fmtDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return ymd || '';
  return `${Number(m[3])}.${Number(m[2])}.${m[1]}`;
}

// Pulsing live indicator dot.
export function LiveDot({ color = TOKENS.green, size = 8 }) {
  return (
    <Box component="span" sx={{ position: 'relative', width: size, height: size, display: 'inline-block', flexShrink: 0 }}>
      <Box component="span" sx={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: color }} />
      <Box
        component="span"
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `2px solid ${color}`,
          '@keyframes wtPulse': {
            '0%': { transform: 'scale(1)', opacity: 0.9 },
            '100%': { transform: 'scale(2.2)', opacity: 0 },
          },
          animation: 'wtPulse 1800ms ease-out infinite',
        }}
      />
    </Box>
  );
}

// Monospace registration plate.
export function Plate({ children, size = 14, color = TOKENS.navy, sx }) {
  return (
    <Box
      component="span"
      sx={{ ...plateFont, fontWeight: 700, color, letterSpacing: '0.02em', fontSize: size, ...sx }}
    >
      {children}
    </Box>
  );
}

// The maintenance states a mechanic can clock into (auto-start eligible + already running).
export const CLOCK_IN_TARGET_STATES = ['planned', 'in_progress', 'waiting', 'vehicle_in_workshop'];
